import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteServiceAccount, type PlainServiceAccount } from "@/queries/service-account-queries";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { ServiceAccountCard } from "@/components/settings/organization/service-accounts/ServiceAccountCard";

export type DeleteServiceAccountDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    serviceAccount: PlainServiceAccount;
    onSuccess?: () => void;
};

export const DeleteServiceAccountDialog: FC<DeleteServiceAccountDialogProps> = ({
    open,
    onOpenChange,
    serviceAccount,
    onSuccess,
}) => {
    const { toast } = useToast();
    const deleteServiceAccount = useDeleteServiceAccount();

    const handleConfirm = useCallback(async () => {
        try {
            await deleteServiceAccount.mutateAsync(serviceAccount.id);
            toast({
                title: "Service account deleted",
                description: `${serviceAccount.name} has been deleted successfully.`,
            });
            onOpenChange(false);
            onSuccess?.();
        } catch (error) {
            toast({
                title: "Failed to delete service account",
                description: formatError(error),
            });
        }
    }, [deleteServiceAccount, serviceAccount.id, serviceAccount.name, toast, onOpenChange, onSuccess]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="delete-service-account-dialog"
                data-track-location={TrackLocations.ServiceAccountDeleteDialog}
                className="max-w-md"
            >
                <DialogHeader>
                    <DialogTitle>Delete Service Account</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to delete this service account? Any workflows or integrations using this
                        service account will stop working.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <ServiceAccountCard serviceAccount={serviceAccount} />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            variant="secondary"
                            disabled={deleteServiceAccount.isPending}
                            data-testid="delete-service-account-dialog-cancel"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        loading={deleteServiceAccount.isPending}
                        disabled={deleteServiceAccount.isPending}
                        data-testid="delete-service-account-dialog-confirm"
                        data-tracking-id="delete-service-account"
                    >
                        Delete
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
